import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { createMenuItems } from "../utils/staffTableHelpers"
import {
  checkStaffDeactivation,
  checkStaffDeletion,
  deactivateStaffMember,
  reactivateStaffMember,
  deleteStaffMemberProfile
} from "store/staff"
import { determineDeactivateMode } from "../../../../../pages/guide/resources/crewMembers/utils/determineDeactivateMode"
import { toaster } from "components/common/Toast"

export const useStaffTable = (t, onEdit, userRole = null, onRefresh = null) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false)
  const [activateModalVisible, setActivateModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [deactivateDeleteMode, setDeactivateDeleteMode] = useState(null)
  const [deactivateDeleteDetails, setDeactivateDeleteDetails] = useState(null)

  const handleView = useCallback(
    (staffId) => {
      navigate(`${staffId}`)
    },
    [navigate]
  )

  const handleEdit = useCallback(
    (staffId) => {
      if (onEdit) {
        onEdit(staffId)
      }
    },
    [onEdit]
  )

  const handleDeactivate = useCallback(
    async (staffMember) => {
      setSelectedStaff(staffMember)
      setDeactivateModalVisible(true)
      try {
        const response = await dispatch(checkStaffDeactivation(staffMember.id))
        const mode = determineDeactivateMode(response)
        setDeactivateDeleteMode(mode)
        setDeactivateDeleteDetails(response)
      } catch (error) {
        console.error("Failed to fetch deactivation details:", error)
        setDeactivateModalVisible(false)
        setSelectedStaff(null)
      }
    },
    [dispatch]
  )

  const handleActivate = useCallback((staffMember) => {
    setSelectedStaff(staffMember)
    setActivateModalVisible(true)
  }, [])

  const handleDelete = useCallback(
    async (staffMember) => {
      setSelectedStaff(staffMember)
      setDeleteModalVisible(true)
      try {
        const response = await dispatch(checkStaffDeletion(staffMember.id))
        const mode = determineDeactivateMode(response)
        setDeactivateDeleteMode(mode)
        setDeactivateDeleteDetails(response)
      } catch (error) {
        console.error("Failed to fetch deletion details:", error)
        setDeleteModalVisible(false)
        setSelectedStaff(null)
      }
    },
    [dispatch]
  )

  const handleDeactivateConfirm = useCallback(
    async (data) => {
      try {
        const response = await dispatch(deactivateStaffMember(selectedStaff?.id, data))
        setDeactivateModalVisible(false)
        setSelectedStaff(null)
        const successMessage = response?.message || "Staff member has been deactivated successfully"
        toaster.success(successMessage)

        // Refresh the staff list
        if (onRefresh) {
          onRefresh()
        }

        return { success: true, response }
      } catch (error) {
        console.error("Failed to deactivate staff:", error)
        return { success: false, error }
      }
    },
    [dispatch, selectedStaff, onRefresh]
  )

  const handleActivateConfirm = useCallback(
    async (data) => {
      try {
        const response = await dispatch(reactivateStaffMember(selectedStaff?.id))
        setActivateModalVisible(false)
        setSelectedStaff(null)
        const successMessage = response?.message || "Staff member has been activated successfully"
        toaster.success(successMessage)

        // Refresh the staff list
        if (onRefresh) {
          onRefresh()
        }

        return { success: true, response }
      } catch (error) {
        console.error("Failed to activate staff:", error)
        return { success: false, error }
      }
    },
    [dispatch, selectedStaff, onRefresh]
  )

  const handleDeleteConfirm = useCallback(
    async (data) => {
      try {
        const response = await dispatch(deleteStaffMemberProfile(selectedStaff?.id, data))
        setDeleteModalVisible(false)
        setSelectedStaff(null)
        const successMessage = response?.message || "Staff member has been deleted successfully"
        toaster.success(successMessage)

        // Refresh the staff list
        if (onRefresh) {
          onRefresh()
        }

        return { success: true, response }
      } catch (error) {
        console.error("Failed to delete staff:", error)
        return { success: false, error }
      }
    },
    [dispatch, selectedStaff, onRefresh]
  )

  const handleModalCancel = useCallback(() => {
    setDeactivateModalVisible(false)
    setActivateModalVisible(false)
    setDeleteModalVisible(false)
    setSelectedStaff(null)
  }, [])

  const getMenuItems = useCallback(
    (staffMember) => {
      const handlers = {
        onEdit: handleEdit,
        onDeactivate: handleDeactivate,
        onActivate: handleActivate,
        onDelete: handleDelete
      }
      return createMenuItems(staffMember, handlers, t, userRole)
    },
    [t, handleEdit, handleDeactivate, handleActivate, handleDelete, userRole]
  )

  return {
    deactivateModalVisible,
    activateModalVisible,
    deleteModalVisible,
    selectedStaff,
    deactivateDeleteMode,
    deactivateDeleteDetails,
    getMenuItems,
    handleView,
    handleDeactivateConfirm,
    handleActivateConfirm,
    handleDeleteConfirm,
    handleModalCancel
  }
}
